import type { PaymentAccountConfig, PaymentMethodConfig, PaymentMethodType } from '@/lib/api/botSettings'

export const PAYMENT_METHOD_OPTIONS: ReadonlyArray<{ value: PaymentMethodType; label: string; hint: string }> = [
  { value: 'pago_movil', label: 'Pago Móvil', hint: 'Banco, teléfono y cédula o RIF' },
  { value: 'transferencia', label: 'Transferencia bancaria', hint: 'Banco, número de cuenta, titular y cédula o RIF' },
  { value: 'efectivo', label: 'Efectivo', hint: 'No necesita datos de cuenta' },
  { value: 'tarjeta', label: 'Tarjeta / Punto de venta', hint: 'No necesita datos de cuenta' },
  { value: 'binance', label: 'Binance Pay', hint: 'Correo o ID de usuario' },
  { value: 'otro', label: 'Otro método', hint: 'Escribe los datos que el cliente necesita' },
]

/** Métodos que no se pagan a una cuenta: no exigen datos bancarios para compartirse. */
export const METHODS_WITHOUT_ACCOUNT: ReadonlySet<PaymentMethodType> = new Set(['efectivo', 'tarjeta'])

export const ACCOUNT_FIELDS: ReadonlyArray<{ key: keyof PaymentAccountConfig; label: string; maxLength: number; inputMode?: 'text' | 'tel' | 'email' }> = [
  { key: 'bankName', label: 'Banco', maxLength: 80 },
  { key: 'accountType', label: 'Tipo de cuenta', maxLength: 40 },
  { key: 'accountNumber', label: 'Número de cuenta', maxLength: 40, inputMode: 'tel' },
  { key: 'holderName', label: 'Titular', maxLength: 100 },
  { key: 'idNumber', label: 'Cédula o RIF', maxLength: 30 },
  { key: 'phone', label: 'Teléfono', maxLength: 30, inputMode: 'tel' },
  { key: 'email', label: 'Correo', maxLength: 120, inputMode: 'email' },
  { key: 'notes', label: 'Notas para el cliente', maxLength: 200 },
]

export const MAX_PAYMENT_METHODS = 10
export const MAX_ACCOUNTS_PER_METHOD = 5

export type PaymentAccountDraft = { id: string } & Record<keyof PaymentAccountConfig, string>

export interface PaymentMethodDraft {
  id: string
  type: PaymentMethodType
  label: string
  currency: string
  instructions: string
  accounts: PaymentAccountDraft[]
}

let counter = 0
const nextId = (prefix: string): string => `${prefix}-${++counter}`

export function emptyAccountDraft(): PaymentAccountDraft {
  return { id: nextId('acc'), bankName: '', accountType: '', accountNumber: '', holderName: '', idNumber: '', phone: '', email: '', notes: '' }
}

export function newMethodDraft(type: PaymentMethodType = 'pago_movil'): PaymentMethodDraft {
  const option = PAYMENT_METHOD_OPTIONS.find((candidate) => candidate.value === type)
  return {
    id: nextId('pm'),
    type,
    label: option?.label ?? 'Método de pago',
    currency: type === 'binance' ? 'USD' : type === 'pago_movil' ? 'VES' : '',
    instructions: '',
    accounts: METHODS_WITHOUT_ACCOUNT.has(type) ? [] : [emptyAccountDraft()],
  }
}

/** Lo guardado en el backend → lo que se edita en pantalla (siempre con textos, nunca `undefined`). */
export function toMethodDrafts(methods: ReadonlyArray<PaymentMethodConfig> | undefined | null): PaymentMethodDraft[] {
  return (methods ?? [])
    .filter((method): method is PaymentMethodConfig => Boolean(method) && typeof method === 'object')
    .map((method) => ({
      id: nextId('pm'),
      type: method.type,
      label: method.label ?? '',
      currency: method.currency ?? '',
      instructions: method.instructions ?? '',
      accounts: (method.accounts ?? []).map((account) => ({ ...emptyAccountDraft(), ...compactStrings(account) })),
    }))
}

function compactStrings(account: PaymentAccountConfig): Partial<PaymentAccountDraft> {
  const out: Partial<PaymentAccountDraft> = {}
  for (const { key } of ACCOUNT_FIELDS) {
    const value = account[key]
    if (typeof value === 'string') out[key] = value
  }
  return out
}

export function accountHasData(account: PaymentAccountDraft): boolean {
  return ACCOUNT_FIELDS.some(({ key }) => account[key].trim().length > 0)
}

/** Lo que se edita en pantalla → lo que recibe el backend (sin cuentas vacías ni textos vacíos). */
export function toMethodPayload(drafts: ReadonlyArray<PaymentMethodDraft>): PaymentMethodConfig[] {
  return drafts.map((draft) => {
    const accounts = draft.accounts.filter(accountHasData).map((account) => {
      const payload: PaymentAccountConfig = {}
      for (const { key } of ACCOUNT_FIELDS) {
        const value = account[key].trim()
        if (value) payload[key] = value
      }
      return payload
    })
    return {
      type: draft.type,
      label: draft.label.trim(),
      currency: draft.currency.trim() || null,
      instructions: draft.instructions.trim() || null,
      accounts,
    }
  })
}

export interface PaymentSettingsErrors {
  /** Error general (no pertenece a un método en particular). */
  form?: string
  /** Error por método, indexado por el `id` del borrador. */
  methods: Record<string, string>
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Validación antes de guardar. Compartir datos bancarios exige que haya al menos un método con datos de cuenta
 * (sin eso el asistente no tendría qué enviar y el cliente recibiría una promesa vacía).
 */
export function validatePaymentSettings(drafts: ReadonlyArray<PaymentMethodDraft>, shareDetails: boolean): PaymentSettingsErrors {
  const errors: PaymentSettingsErrors = { methods: {} }

  for (const draft of drafts) {
    if (!draft.label.trim()) {
      errors.methods[draft.id] = 'Escribe el nombre del método para que el cliente lo reconozca.'
      continue
    }
    const badEmail = draft.accounts.find((account) => account.email.trim() && !EMAIL_PATTERN.test(account.email.trim()))
    if (badEmail) errors.methods[draft.id] = 'Revisa el correo: debe tener el formato nombre@dominio.com.'
  }

  if (shareDetails) {
    const hasAccountData = drafts.some((draft) => draft.accounts.some(accountHasData))
    if (!hasAccountData) {
      errors.form = 'Para que el asistente pueda enviar datos bancarios, carga al menos una cuenta (por ejemplo, un Pago Móvil con teléfono y cédula).'
    }
  }
  return errors
}

export function hasPaymentErrors(errors: PaymentSettingsErrors): boolean {
  return Boolean(errors.form) || Object.keys(errors.methods).length > 0
}

export function countMethodsWithAccountData(drafts: ReadonlyArray<PaymentMethodDraft>): number {
  return drafts.filter((draft) => draft.accounts.some(accountHasData)).length
}
